import { getFullName, useCurrentUserQuery, useOrgRolesQuery } from '@alice-financial/alice-sdk'
import {
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  PII,
  SwitchLanguage,
  Typography,
  useMediaQuery,
  useTheme,
} from '@alice-financial/pretext-ui'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/MoreVert'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useGetHelp } from '../employeeDashboard/ContactSupport'
import { useIsEnrolling } from '../enrollment/useEnrollmentStatus'
import { useProfileTypeContext } from './sessionProfile/SessionProfileContext'
import { useHasProfileType } from './sessionProfile/sessionProfileHelpers'
import { AvailableProfileTypes, PROFILE_TYPES } from './sessionProfile/types'

// hook to determine whether the current user has a required profile type but is not currently using it
const useProfileSwitch = (...requiredProfileTypes: AvailableProfileTypes) => {
  const currentProfileType = useProfileTypeContext().current
  const hasRequiredProfileType = useHasProfileType(...requiredProfileTypes)
  if (!currentProfileType) return false

  return hasRequiredProfileType && !requiredProfileTypes.includes(currentProfileType)
}

// menu item to switch to a different profile type
const ProfileSwitchMenuItem = ({ onClick }: { onClick: () => void }) => {
  const currentProfileType = useProfileTypeContext().current
  const showBrokerProfileSwitch = useProfileSwitch('multi_admin')
  const showPartnerProfileSwitch = useProfileSwitch('payroll_partner')
  const showAdminProfileSwitch =
    useProfileSwitch('organization_admin') &&
    !showBrokerProfileSwitch &&
    !showPartnerProfileSwitch &&
    currentProfileType !== 'multi_admin' &&
    currentProfileType !== 'payroll_partner'
  const showEmployeeProfileSwitch = useProfileSwitch('employee')
  return (
    <>
      {showBrokerProfileSwitch && (
        <MenuItem onClick={onClick} component={Link} to="/manage">
          <FormattedMessage id="menu.switch_to_broker" />
        </MenuItem>
      )}
      {showAdminProfileSwitch && (
        <MenuItem onClick={onClick} component={Link} to="/manage">
          <FormattedMessage id="menu.switch_to_admin" />
        </MenuItem>
      )}
      {showEmployeeProfileSwitch && (
        <MenuItem onClick={onClick} component={Link} to="/dashboard">
          <FormattedMessage id="menu.switch_to_employee" />
        </MenuItem>
      )}
      {showPartnerProfileSwitch && (
        <MenuItem onClick={onClick} component={Link} to="/manage">
          <FormattedMessage id="menu.switch_to_payroll_partner" />
        </MenuItem>
      )}
    </>
  )
}

const GetHelpMenuItem = ({ handleClose }: { handleClose: () => void }) => {
  const { openHelp } = useGetHelp()
  const onClick = () => {
    handleClose()
    openHelp()
  }
  return (
    <MenuItem onClick={onClick}>
      <FormattedMessage id="contact_support.get_help" />
    </MenuItem>
  )
}

const ProfileType = () => {
  const { data: orgRolesData } = useOrgRolesQuery()
  const showAdminProfileSwitch = useProfileSwitch('organization_admin')
  const showEmployeeProfileSwitch = useProfileSwitch('employee')
  const showBrokerProfileSwitch = useProfileSwitch('multi_admin')
  const showPartnerProfileSwitch = useProfileSwitch('payroll_partner')
  const currentProfileType = useProfileTypeContext().current
  if (
    (!showAdminProfileSwitch &&
      !showEmployeeProfileSwitch &&
      !showBrokerProfileSwitch &&
      !showPartnerProfileSwitch) ||
    !currentProfileType
  ) {
    return null
  }
  const label =
    orgRolesData?.currentUser?.adminRoles[0]?.label?.split(',')[0] || PROFILE_TYPES[currentProfileType]
  return (
    <Typography component="span" fontSize="0.875em">
      ({label})
    </Typography>
  )
}

/**
 * This is a menu that is intended to be put inside an AppBar/Header component. It displays
 * the user's avatar (or initials) and will show a menu when clicked. This menu will link to
 * the user's profile page and provide a logout link.
 */
export const AvatarMenu = ({ compact }: { compact?: boolean }) => {
  const theme = useTheme()
  const isMobileWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const isCompact = compact || isMobileWidth

  const { data: currentUserData } = useCurrentUserQuery()
  const isEnrolling = useIsEnrolling()
  const currentProfileType = useProfileTypeContext().current

  const menuAnchor = React.useRef<HTMLButtonElement | null>(null)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleMenu = () => setAnchorEl(menuAnchor.current)
  const handleClose = () => setAnchorEl(null)

  if (!currentUserData?.currentUser) return <SwitchLanguage compact={isCompact} />

  const { currentUser } = currentUserData

  return (
    <>
      {isEnrolling && currentProfileType === 'user' && <SwitchLanguage compact={isCompact} />}
      {isCompact ? (
        <IconButton color="primary" ref={menuAnchor} onClick={handleMenu}>
          <MenuIcon fontSize="large" />
        </IconButton>
      ) : (
        <Button
          variant="text"
          ref={menuAnchor}
          onClick={handleMenu}
          endIcon={<MenuIcon fontSize="large" />}
          sx={{ textWrap: 'nowrap' }}
        >
          <span>
            <PII>{getFullName(currentUser)}</PII> <ProfileType />
          </span>
        </Button>
      )}
      <Menu
        id="avatar-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <ProfileSwitchMenuItem onClick={handleClose} />
        {!isEnrolling && (
          <MenuItem onClick={handleClose} component={Link} to="/profile">
            <FormattedMessage id="menu.profile" />
          </MenuItem>
        )}
        {!isEnrolling && (
          <MenuItem onClick={handleClose} component={Link} to="/dashboard/documents">
            <FormattedMessage id="menu.documents" />
          </MenuItem>
        )}
        <GetHelpMenuItem handleClose={handleClose} />
        <MenuItem
          onClick={handleClose}
          component={Link}
          to="/logout"
          sx={{ display: 'flex', justifyContent: 'space-between', columnGap: '0.5em' }}
        >
          <FormattedMessage id="menu.logout" />
          <LogoutIcon fontSize="small" color="primary" />
        </MenuItem>
      </Menu>
    </>
  )
}
